import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { TimePicker } from '@/components/ui/time-picker';
import { StepSection } from '@/components/step-section';
import { TextField } from '@/components/text-field';
import { ApiError } from '@/lib/api';
import { field_errors_from, form_error_message } from '@/lib/forms';
import { use_draft } from '@/features/onboarding/hooks';
import { next_module_step_after } from '@/features/onboarding/module-step-router';
import { use_cafe_details, use_save_cafe_details } from '@/features/cafe/hooks';

const HH_MM = /^([01]\d|2[0-3]):[0-5]\d$/;

type Form = {
  opening_time: string;
  closing_time: string;
  years_active: string;
};

const EMPTY: Form = {
  opening_time: '09:00',
  closing_time: '22:00',
  years_active: '',
};

function to_minutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
}

export function ModuleCafePage() {
  const navigate = useNavigate();
  const draft_q = use_draft();
  const saved_q = use_cafe_details({ enabled: !!draft_q.data });
  const save_m = use_save_cafe_details();
  const [form, set_form] = useState<Form>(EMPTY);
  const [prefilled, set_prefilled] = useState(false);
  const [error, set_error] = useState<string | null>(null);
  const [field_errors, set_field_errors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!draft_q.isFetched) return;
    if (!draft_q.data) {
      navigate('/onboarding/start', { replace: true });
      return;
    }
    if (!draft_q.data.address_completed) {
      navigate('/onboarding/address', { replace: true });
      return;
    }
    if (!draft_q.data.module_types.includes('CAFE')) {
      // Vendor didn't select CAFE — bounce forward.
      navigate(next_module_step_after('CAFE', draft_q.data.module_types), {
        replace: true,
      });
    }
  }, [draft_q.isFetched, draft_q.data, navigate]);

  useEffect(() => {
    if (prefilled) return;
    if (saved_q.data) {
      set_form({
        opening_time: saved_q.data.opening_time,
        closing_time: saved_q.data.closing_time,
        years_active:
          saved_q.data.years_active !== null ? String(saved_q.data.years_active) : '',
      });
      set_prefilled(true);
    }
  }, [saved_q.data, prefilled]);

  function update<K extends keyof Form>(key: K, value: Form[K]) {
    set_form((f) => ({ ...f, [key]: value }));
  }

  async function handle_submit(e: React.FormEvent) {
    e.preventDefault();
    set_error(null);

    if (!HH_MM.test(form.opening_time) || !HH_MM.test(form.closing_time)) {
      set_error('Opening and closing times must be in 24h HH:mm format.');
      return;
    }
    if (to_minutes(form.opening_time) >= to_minutes(form.closing_time)) {
      set_error('Opening time must be strictly before closing time.');
      return;
    }

    let years_active: number | undefined;
    if (form.years_active.trim() !== '') {
      const n = Number(form.years_active);
      if (!Number.isInteger(n) || n < 0 || n > 100) {
        set_error('Years active must be a whole number between 0 and 100.');
        return;
      }
      years_active = n;
    }

    try {
      await save_m.mutateAsync({
        opening_time: form.opening_time,
        closing_time: form.closing_time,
        years_active,
      });
      if (!draft_q.data) return;
      navigate(next_module_step_after('CAFE', draft_q.data.module_types));
    } catch (err) {
      set_field_errors(field_errors_from(err));
      set_error(
        err instanceof ApiError
          ? form_error_message(err, 'Could not save café details', { highlights_fields: true })
          : 'Could not save café details',
      );
    }
  }

  return (
    <form onSubmit={handle_submit}>
      <StepSection
        step_key="module-cafe"
        title="Pet Café details"
        description="Operating hours customers will see in search. Both times are local."
        error={error}
        footer={
          <Button
            type="submit"
            variant="gradient"
            disabled={save_m.isPending}
            data-testid="module-cafe-submit"
          >
            {save_m.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Save & continue
          </Button>
        }
      >
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <div className="flex flex-col gap-2">
            <Label htmlFor="opening_time">Opening time</Label>
            <TimePicker
              id="opening_time"
              name="opening_time"
              data-testid="field-opening-time"
              value={form.opening_time}
              onChange={(v) => update('opening_time', v)}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="closing_time">Closing time</Label>
            <TimePicker
              id="closing_time"
              name="closing_time"
              data-testid="field-closing-time"
              value={form.closing_time}
              onChange={(v) => update('closing_time', v)}
            />
          </div>
        </div>
        <TextField
          label="Years active"
          name="years_active"
          error={field_errors.years_active}
          data-testid="field-years-active"
          value={form.years_active}
          onChange={(e) =>
            update('years_active', e.target.value.replace(/\D/g, '').slice(0, 3))
          }
          inputMode="numeric"
          placeholder="3"
          optional
          hint="Whole years your café has been operating. Skip if unknown."
        />
      </StepSection>
    </form>
  );
}
